import { FC, useMemo, useState } from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import { PerfectOrderNoteResponse } from 'src/types/perfectOrder';
import { UserSimple } from 'src/types/userSimple';

interface Props {
  notes: PerfectOrderNoteResponse[];
  users?: UserSimple[];
  onNoteChange: (perfectOrderNoteId: number, value: string) => void;
  onAppendNote: () => void;
  onSaveNote: (perfectOrderNoteId: number) => void;
  onCancelNewNote: (perfectOrderNoteId: number) => void;
  onUpdateNote: (perfectOrderNoteId: number, value: string) => Promise<void> | void;
  disabled?: boolean;
}

const PerfectOrderNotesGrid: FC<Props> = ({ notes, users = [], onNoteChange, onAppendNote, onSaveNote, onCancelNewNote, onUpdateNote, disabled = false }) => {
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const sortedNotes = useMemo(() => {
    return [...(notes || [])].sort((a, b) => {
      const aDate = a.createDate ? new Date(a.createDate).getTime() : 0;
      const bDate = b.createDate ? new Date(b.createDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [notes]);

  const usersById = useMemo(() => new Map(users.map((user) => [user.companyUserId, user])), [users]);

  const getCreatedByDisplay = (createdBy?: number | null) => {
    if (!createdBy) {
      return '';
    }

    const user = usersById.get(createdBy);
    return user?.username || String(createdBy);
  };

  const startEdit = (note: PerfectOrderNoteResponse) => {
    if (disabled) {
      return;
    }

    setEditingNoteId(note.perfectOrderNoteId);
    setEditDraft(note.note ?? '');
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditDraft('');
  };

  const saveEdit = async (perfectOrderNoteId: number) => {
    if (disabled) {
      return;
    }

    const trimmed = editDraft.trim();
    if (!trimmed) {
      return;
    }

    setSavingEdit(true);
    try {
      await onUpdateNote(perfectOrderNoteId, trimmed);
      setEditingNoteId(null);
      setEditDraft('');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography component="h4" variant="h6" className="mb-0">
          Notes
        </Typography>
        <Button variant="outlined" size="small" disabled={disabled} onClick={onAppendNote}>
          Create Note
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          {/* <TableHead>
            <TableRow>
              <TableCell>Perfect Order Notes</TableCell>
            </TableRow>
          </TableHead> */}
          <TableBody>
            {sortedNotes.map((note) => {
              const isNew = (note.perfectOrderNoteId ?? 0) <= 0;
              const isEditing = !isNew && editingNoteId === note.perfectOrderNoteId;
              const createdByDisplay = getCreatedByDisplay(note.createdBy);

              return (
                <TableRow key={note.perfectOrderNoteId}>
                  <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {note.createDate ? moment(note.createDate).format('M/DD/YYYY h:mm A') : ''}
                          {createdByDisplay ? ` by ${createdByDisplay}` : ''}
                        </Typography>

                        {isNew && (
                          <>
                            <TextField
                              value={note.note ?? ''}
                              onChange={(event) => onNoteChange(note.perfectOrderNoteId, event.target.value)}
                              size="small"
                              fullWidth
                              multiline
                              minRows={4}
                              autoFocus
                              disabled={disabled}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button variant="contained" size="small" disabled={disabled || !(note.note || '').trim()} onClick={() => onSaveNote(note.perfectOrderNoteId)}>
                                Save
                              </Button>
                              <Button variant="text" size="small" onClick={() => onCancelNewNote(note.perfectOrderNoteId)}>
                                Cancel
                              </Button>
                            </Box>
                          </>
                        )}

                        {isEditing && (
                          <>
                            <TextField
                              value={editDraft}
                              onChange={(event) => setEditDraft(event.target.value)}
                              size="small"
                              fullWidth
                              multiline
                              minRows={4}
                              autoFocus
                              disabled={disabled || savingEdit}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button variant="contained" size="small" disabled={disabled || savingEdit || !editDraft.trim()} onClick={() => saveEdit(note.perfectOrderNoteId)}>
                                Save
                              </Button>
                              <Button variant="text" size="small" disabled={savingEdit} onClick={cancelEdit}>
                                Cancel
                              </Button>
                            </Box>
                          </>
                        )}

                        {!isNew && !isEditing && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {note.note}
                          </Typography>
                        )}
                      </Box>

                      {!isNew && !isEditing && (
                        <Tooltip title="Edit note">
                          <IconButton size="small" disabled={disabled} onClick={() => startEdit(note)} aria-label="Edit note">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedNotes.length === 0 && (
              <TableRow>
                <TableCell align="center">No notes found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PerfectOrderNotesGrid;
